import { Chips, IChipsOption } from '@cian/ui-kit';
import { useCallback, useRef } from 'react';
import { NonEmptyArray } from '../../../../../packages/JsonQuery';
import { InlineFilter } from 'shared/components/InlineFilter';
import { useUpdateEffect } from '@cian/react-utils';
import { formatToJsonQueryTermsValue } from 'shared/utils/jsonQuery';

interface IMultiSelectionAdvancedFilterProps<T> {
  value: (T | null)[];
  options: IChipsOption<T | null>[];
  onFilterChange: (newValue: NonEmptyArray<T> | null) => void;
}

export function MultiSelectionAdvancedFilter<T>({
  value,
  options,
  onFilterChange,
}: IMultiSelectionAdvancedFilterProps<T>) {
  const prevValueRef = useRef(formatToJsonQueryTermsValue(value));

  const applyFilter = useCallback(
    (newValue: NonEmptyArray<T> | null) => {
      onFilterChange(newValue);
      prevValueRef.current = newValue;
    },
    [onFilterChange],
  );

  const handleChipClick = useCallback(
    (newValue: T | null, checked: boolean) => {
      if (newValue === null) {
        applyFilter(null);

        return;
      }

      const prevValue = prevValueRef.current || [];

      const nextValue = checked ? [...prevValue, newValue] : prevValue.filter(item => item !== newValue);

      applyFilter(formatToJsonQueryTermsValue(nextValue));
    },
    [applyFilter],
  );

  useUpdateEffect(() => {
    prevValueRef.current = formatToJsonQueryTermsValue(value);
  }, [value]);

  return (
    <InlineFilter>
      <Chips options={options} value={value} onChipClick={handleChipClick} selectType="multiple" size="S" />
    </InlineFilter>
  );
}
